import React from 'react';
import { motion } from 'framer-motion';
import { Plug, Settings, Zap, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const stepIcons = [Plug, Settings, Zap];
const stepColors = ['from-indigo-500 to-indigo-600', 'from-cyan-500 to-cyan-600', 'from-indigo-600 to-cyan-600'];

const HowItWorks = () => {
  const { t } = useTranslation('zimbra-admin-panel');
  const steps = t('howItWorks.steps', { returnObjects: true }) as Array<{ title: string; description: string }>;

  return (
    <section className="py-24 bg-gray-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('howItWorks.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            {t('howItWorks.subtitle')}
          </p>
        </motion.div>

        <div className="relative">
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-cyan-500 to-indigo-600 mx-auto" style={{ width: 'calc(100% - 200px)', left: '100px' }} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 relative">
            {steps.map((step, index) => {
              const Icon = stepIcons[index] ?? Plug;
              return (
                <React.Fragment key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 }}
                    className="relative"
                  >
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-indigo-600 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg z-10">
                      {index + 1}
                    </div>

                    <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-slate-800 h-full">
                      <div className={`w-16 h-16 bg-gradient-to-br ${stepColors[index]} rounded-xl flex items-center justify-center mb-6 shadow-lg`}>
                        <Icon className="h-8 w-8 text-white" />
                      </div>

                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        {step.title}
                      </h3>

                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>

                  {index < steps.length - 1 && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.2 + 0.3 }}
                      className="hidden lg:flex absolute top-24 items-center justify-center text-indigo-500"
                      style={{
                        left: `${((index + 1) * 100) / 3 - 5}%`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      <ArrowRight className="h-8 w-8" />
                    </motion.div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <button className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-cyan-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-cyan-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105">
            {t('howItWorks.cta')}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
